import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/Shared/ErrorBoundary';

// Suppress browser extension errors EARLY - before any other code runs
// These errors are harmless and come from browser extensions (e.g., LastPass, Grammarly, Redux DevTools)
if (typeof window !== 'undefined') {
  // Set up unhandled rejection handler FIRST, before anything else
  // This must be the FIRST handler to catch errors early
  const earlyRejectionHandler = (event: PromiseRejectionEvent) => {
    try {
      // Get error in multiple formats to catch all variations
      const errorMessage = event.reason?.message || event.reason?.toString() || String(event.reason) || '';
      const errorString = JSON.stringify(event.reason) || '';
      const errorStack = event.reason?.stack || '';
      const errorName = event.reason?.name || '';
      const allErrorText = `${errorMessage} ${errorString} ${errorStack} ${errorName}`.toLowerCase();
      
      // Check for async response errors (most common browser extension error)
      // Match: "A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received"
      // Be very aggressive - if ANY part of the error message matches, suppress it
      const isAsyncResponseError = 
        allErrorText.includes('listener indicated') ||
        allErrorText.includes('asynchronous response') ||
        allErrorText.includes('message channel closed') ||
        allErrorText.includes('message channel') ||
        (allErrorText.includes('returning true') && allErrorText.includes('message channel')) ||
        // Also check individual parts - if error contains any of these, it's likely this error
        (errorMessage.toLowerCase().includes('listener') && errorMessage.toLowerCase().includes('asynchronous')) ||
        (errorMessage.toLowerCase().includes('message channel') && errorMessage.toLowerCase().includes('closed'));
      
      // Check for other browser extension errors
      const isBrowserExtensionError = 
        isAsyncResponseError ||
        allErrorText.includes('no tab with id') ||
        allErrorText.includes('background-redux') ||
        allErrorText.includes('chrome-extension://') ||
        allErrorText.includes('content-script') ||
        allErrorText.includes('runtime.lasterror') ||
        allErrorText.includes('unchecked runtime.lasterror');
      
      if (isBrowserExtensionError) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // Return false to indicate we handled it
        return false;
      }
    } catch (e) {
      // Silently ignore errors in error handler
    }
  };
  
  // Add with capture phase to catch early - this runs before other handlers
  window.addEventListener('unhandledrejection', earlyRejectionHandler, true);
  // Add a helpful message explaining browser extension errors (only in development)
  if (process.env.NODE_ENV === 'development') {
    // Use setTimeout to ensure this runs after browser extension errors are logged
    setTimeout(() => {
      console.log(
        '%cℹ️ Browser Extension Errors',
        'color: #2196F3; font-weight: bold; font-size: 12px;'
      );
      console.log(
        '%cIf you see errors like "No tab with id", "Unchecked runtime.lastError", or "Cannot find menu item" in the console, these are harmless browser extension errors (LastPass, Redux DevTools, etc.) and can be safely ignored. They do not affect the app\'s functionality.',
        'color: #666; font-size: 11px; line-height: 1.4;'
      );
    }, 1000);
  }
  // Also suppress at console level to prevent console logging
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    const errorMessage = args[0]?.toString() || '';
    const allArgs = args.join(' ').toLowerCase();
    const lowerErrorMessage = errorMessage.toLowerCase();
    
    // Check for async response errors at console level too
    // Be very aggressive - if ANY key phrase appears, suppress it
    const isAsyncResponseError = 
      lowerErrorMessage.includes('listener indicated') ||
      lowerErrorMessage.includes('asynchronous response') ||
      lowerErrorMessage.includes('message channel closed') ||
      (lowerErrorMessage.includes('message channel') && (lowerErrorMessage.includes('closed') || lowerErrorMessage.includes('listener') || lowerErrorMessage.includes('asynchronous'))) ||
      (lowerErrorMessage.includes('listener') && lowerErrorMessage.includes('asynchronous')) ||
      allArgs.includes('listener indicated') ||
      allArgs.includes('asynchronous response') ||
      allArgs.includes('message channel closed') ||
      (allArgs.includes('message channel') && (allArgs.includes('closed') || allArgs.includes('listener') || allArgs.includes('asynchronous'))) ||
      (allArgs.includes('listener') && allArgs.includes('asynchronous'));
    
    if (isAsyncResponseError) {
      return; // Suppress this error
    }
    
    // Check if this is a browser extension error (check chrome-extension:// URLs)
    // Also catch "Unchecked runtime.lastError" errors from LastPass and other extensions
    const isBrowserExtensionError = 
      lowerErrorMessage.includes('listener indicated') ||
      lowerErrorMessage.includes('asynchronous response') ||
      lowerErrorMessage.includes('message channel') ||
      lowerErrorMessage.includes('by returning true') ||
      lowerErrorMessage.includes('runtime.lasterror') ||
      lowerErrorMessage.includes('unchecked runtime.lasterror') ||
      lowerErrorMessage.includes('runtime.lasterror') ||
      lowerErrorMessage.includes('unchecked runtime.lasterror') ||
      lowerErrorMessage.includes('cannot create item') ||
      lowerErrorMessage.includes('duplicate id') ||
      (lowerErrorMessage.includes('unchecked runtime') && lowerErrorMessage.includes('cannot create item')) ||
      (lowerErrorMessage.includes('unchecked runtime') && lowerErrorMessage.includes('duplicate id')) ||
      (lowerErrorMessage.includes('runtime.last') && lowerErrorMessage.includes('cannot create item')) ||
      (lowerErrorMessage.includes('runtime.last') && lowerErrorMessage.includes('duplicate id')) ||
      lowerErrorMessage.includes('cannot find menu item') ||
      lowerErrorMessage.includes('no tab with id') ||
      lowerErrorMessage.includes('background-redux') ||
      lowerErrorMessage.includes('$ is not defined') ||
      lowerErrorMessage.includes('referenceerror: $') ||
      allArgs.includes('listener indicated') ||
      allArgs.includes('asynchronous response') ||
      allArgs.includes('message channel') ||
      allArgs.includes('by returning true') ||
      allArgs.includes('runtime.lasterror') ||
      allArgs.includes('unchecked runtime.lasterror') ||
      allArgs.includes('runtime.lasterror') ||
      allArgs.includes('unchecked runtime.lasterror') ||
      allArgs.includes('cannot create item') ||
      allArgs.includes('duplicate id') ||
      (allArgs.includes('unchecked runtime') && allArgs.includes('cannot create item')) ||
      (allArgs.includes('unchecked runtime') && allArgs.includes('duplicate id')) ||
      (allArgs.includes('runtime.last') && allArgs.includes('cannot create item')) ||
      (allArgs.includes('runtime.last') && allArgs.includes('duplicate id')) ||
      allArgs.includes('cannot find menu item') ||
      allArgs.includes('no tab with id') ||
      allArgs.includes('background-redux') ||
      allArgs.includes('chrome-extension://') ||
      allArgs.includes('content-script') ||
      allArgs.includes('ch-content-script') ||
      allArgs.includes('lastpass');
    
    // Suppress cache errors for audio files (harmless browser cache warnings)
    // These errors don't prevent the audio from working
    // Handle URL-encoded filenames (e.g., boxing-bell-signals-6115%20(1).mp3)
    const isCacheError = 
      lowerErrorMessage.includes('err_cache_operation_not_supported') ||
      lowerErrorMessage.includes('net::err_cache') ||
      (lowerErrorMessage.includes('failed to load resource') && (lowerErrorMessage.includes('cache') || lowerErrorMessage.includes('err_cache'))) ||
      allArgs.includes('err_cache_operation_not_supported') ||
      allArgs.includes('net::err_cache') ||
      (allArgs.includes('failed to load resource') && (allArgs.includes('cache') || allArgs.includes('err_cache'))) ||
      (allArgs.includes('boxing-bell') && (allArgs.includes('cache') || allArgs.includes('err_cache') || allArgs.includes('failed to load')));
    
    // Suppress Supabase refresh token 400 errors (expected when sessions expire)
    const isRefreshTokenError = 
      (lowerErrorMessage.includes('400') || allArgs.includes('400')) &&
      (lowerErrorMessage.includes('refresh') || allArgs.includes('refresh') ||
       lowerErrorMessage.includes('grant_type') || allArgs.includes('grant_type') ||
       lowerErrorMessage.includes('/auth/v1/token') || allArgs.includes('/auth/v1/token'));
    
    // Don't log browser extension errors, cache errors, or refresh token errors
    if (isBrowserExtensionError || isCacheError || isRefreshTokenError) {
      return;
    }
    
    // Log all other errors normally
    originalConsoleError.apply(console, args);
  };
  
  // Also suppress console.warn for browser extension errors (LastPass sometimes uses warn)
  const originalConsoleWarn = console.warn;
  console.warn = (...args: any[]) => {
    const errorMessage = args[0]?.toString() || '';
    const allArgs = args.join(' ').toLowerCase();
    const lowerErrorMessage = errorMessage.toLowerCase();
    
    // Check for async response errors at warn level too
    const isAsyncResponseErrorWarn = 
      lowerErrorMessage.includes('listener indicated') ||
      lowerErrorMessage.includes('asynchronous response') ||
      lowerErrorMessage.includes('message channel closed') ||
      (lowerErrorMessage.includes('message channel') && (lowerErrorMessage.includes('closed') || lowerErrorMessage.includes('listener'))) ||
      (lowerErrorMessage.includes('listener') && lowerErrorMessage.includes('asynchronous')) ||
      allArgs.includes('listener indicated') ||
      allArgs.includes('asynchronous response') ||
      allArgs.includes('message channel closed') ||
      (allArgs.includes('message channel') && (allArgs.includes('closed') || allArgs.includes('listener'))) ||
      (allArgs.includes('listener') && allArgs.includes('asynchronous'));
    
    // Check if this is a browser extension error
    const isBrowserExtensionError = 
      isAsyncResponseErrorWarn ||
      lowerErrorMessage.includes('unchecked runtime.lasterror') ||
      lowerErrorMessage.includes('runtime.lasterror') ||
      lowerErrorMessage.includes('cannot create item') ||
      lowerErrorMessage.includes('duplicate id') ||
      (lowerErrorMessage.includes('unchecked runtime') && lowerErrorMessage.includes('duplicate id')) ||
      lowerErrorMessage.includes('cannot find menu item') ||
      allArgs.includes('unchecked runtime.lasterror') ||
      allArgs.includes('runtime.lasterror') ||
      allArgs.includes('cannot create item') ||
      allArgs.includes('duplicate id') ||
      (allArgs.includes('unchecked runtime') && allArgs.includes('duplicate id')) ||
      allArgs.includes('cannot find menu item') ||
      allArgs.includes('lastpass');
    
    // Don't log browser extension errors
    if (isBrowserExtensionError) {
      return;
    }
    
    // Log all other warnings normally
    originalConsoleWarn.apply(console, args);
  };
  
  // Suppress unhandled promise rejections from browser extensions
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';
    const errorString = JSON.stringify(event.reason) || '';
    const errorStack = event.reason?.stack || '';
    const errorName = event.reason?.name || '';
    const allErrorText = `${errorMessage} ${errorString} ${errorStack} ${errorName}`.toLowerCase();
    const lowerErrorMessage = errorMessage.toLowerCase();
    const lowerErrorString = errorString.toLowerCase();
    const lowerErrorStack = errorStack.toLowerCase();
    
    // Check if error is from background-redux-new.js (common Redux DevTools error)
    const isReduxDevToolsError = 
      lowerErrorStack.includes('background-redux-new.js') ||
      lowerErrorString.includes('background-redux-new.js') ||
      (lowerErrorMessage.includes('no tab with id') && (lowerErrorStack.includes('background-redux') || lowerErrorString.includes('background-redux')));
    
    // Check for specific "listener indicated asynchronous response" error pattern
    // Match: "A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received"
    // Be very aggressive - check for any combination of key phrases
    const isAsyncResponseError = 
      lowerErrorMessage.includes('listener indicated') ||
      lowerErrorMessage.includes('asynchronous response') ||
      lowerErrorMessage.includes('message channel closed') ||
      (lowerErrorMessage.includes('listener') && lowerErrorMessage.includes('asynchronous')) ||
      (lowerErrorMessage.includes('message channel') && (lowerErrorMessage.includes('closed') || lowerErrorMessage.includes('listener'))) ||
      lowerErrorString.includes('listener indicated') ||
      lowerErrorString.includes('asynchronous response') ||
      lowerErrorString.includes('message channel closed') ||
      (lowerErrorString.includes('listener') && lowerErrorString.includes('asynchronous')) ||
      (lowerErrorString.includes('message channel') && (lowerErrorString.includes('closed') || lowerErrorString.includes('listener'))) ||
      allErrorText.includes('listener indicated') ||
      allErrorText.includes('asynchronous response') ||
      allErrorText.includes('message channel closed') ||
      (allErrorText.includes('listener') && allErrorText.includes('asynchronous')) ||
      (allErrorText.includes('message channel') && (allErrorText.includes('closed') || allErrorText.includes('listener'))) ||
      (allErrorText.includes('listener indicated') && allErrorText.includes('returning true') && allErrorText.includes('message channel'));
    
    // More comprehensive check - catch any variation of browser extension errors
    // Check for partial matches to catch all variations (case-insensitive)
    // Also check if error is from route paths (e.g., /rules, /login, etc.)
    const isFromRoute = /^\/?\d*[a-z]+/.test(errorMessage) || 
                        lowerErrorMessage.includes('/rules') ||
                        lowerErrorMessage.includes('/login') ||
                        lowerErrorMessage.includes('/matchmaking') ||
                        lowerErrorString.includes('/rules') ||
                        lowerErrorString.includes('/login') ||
                        lowerErrorString.includes('/matchmaking');
    
    const isBrowserExtensionError = 
      isReduxDevToolsError ||
      isAsyncResponseError ||
      lowerErrorMessage.includes('$ is not defined') ||
      lowerErrorMessage.includes('referenceerror: $') ||
      (errorName === 'ReferenceError' && (lowerErrorMessage.includes('$') || lowerErrorString.includes('$'))) ||
      lowerErrorStack.includes('content-script') ||
      lowerErrorStack.includes('ch-content-script') ||
      lowerErrorStack.includes('chrome-extension://') ||
      lowerErrorMessage.includes('listener indicated') ||
      lowerErrorMessage.includes('asynchronous response') ||
      lowerErrorMessage.includes('message channel') ||
      lowerErrorMessage.includes('by returning true') ||
      (lowerErrorMessage.includes('by returning true') && lowerErrorMessage.includes('message channel closed')) ||
      lowerErrorMessage.includes('runtime.lasterror') ||
      lowerErrorMessage.includes('unchecked runtime.lasterror') ||
      lowerErrorMessage.includes('cannot create item') ||
      lowerErrorMessage.includes('duplicate id') ||
      (lowerErrorMessage.includes('unchecked runtime') && lowerErrorMessage.includes('duplicate id')) ||
      lowerErrorMessage.includes('cannot find menu item') ||
      lowerErrorMessage.includes('no tab with id') ||
      lowerErrorMessage.includes('background-redux') ||
      (lowerErrorMessage.includes('no tab with id') && lowerErrorMessage.includes('background-redux')) ||
      lowerErrorString.includes('listener indicated') ||
      lowerErrorString.includes('asynchronous response') ||
      lowerErrorString.includes('message channel') ||
      lowerErrorString.includes('by returning true') ||
      (lowerErrorString.includes('by returning true') && lowerErrorString.includes('message channel closed')) ||
      lowerErrorString.includes('runtime.lasterror') ||
      lowerErrorString.includes('unchecked runtime.lasterror') ||
      lowerErrorString.includes('cannot create item') ||
      lowerErrorString.includes('duplicate id') ||
      (lowerErrorString.includes('unchecked runtime') && lowerErrorString.includes('duplicate id')) ||
      lowerErrorString.includes('cannot find menu item') ||
      lowerErrorString.includes('lastpass') ||
      lowerErrorString.includes('no tab with id') ||
      lowerErrorString.includes('background-redux') ||
      lowerErrorString.includes('background-redux-new.js') ||
      lowerErrorString.includes('chrome-extension://') ||
      lowerErrorStack.includes('listener indicated') ||
      lowerErrorStack.includes('message channel') ||
      lowerErrorStack.includes('by returning true') ||
      (lowerErrorStack.includes('by returning true') && lowerErrorStack.includes('message channel closed')) ||
      lowerErrorStack.includes('runtime.lasterror') ||
      lowerErrorStack.includes('unchecked runtime.lasterror') ||
      lowerErrorStack.includes('cannot create item') ||
      lowerErrorStack.includes('duplicate id') ||
      (lowerErrorStack.includes('unchecked runtime') && lowerErrorStack.includes('duplicate id')) ||
      lowerErrorStack.includes('cannot find menu item') ||
      lowerErrorStack.includes('background-redux') ||
      lowerErrorStack.includes('background-redux-new.js') ||
      lowerErrorStack.includes('chrome-extension://') ||
      lowerErrorStack.includes('content-script') ||
      lowerErrorStack.includes('ch-content-script') ||
      allErrorText.includes('listener indicated') ||
      allErrorText.includes('asynchronous response') ||
      allErrorText.includes('message channel') ||
      allErrorText.includes('by returning true') ||
      (allErrorText.includes('by returning true') && allErrorText.includes('message channel closed')) ||
      allErrorText.includes('runtime.lasterror') ||
      allErrorText.includes('unchecked runtime.lasterror') ||
      allErrorText.includes('cannot create item') ||
      allErrorText.includes('duplicate id') ||
      (allErrorText.includes('unchecked runtime') && allErrorText.includes('duplicate id')) ||
      allErrorText.includes('cannot find menu item') ||
      allErrorText.includes('no tab with id') ||
      allErrorText.includes('background-redux') ||
      allErrorText.includes('background-redux-new.js') ||
      (allErrorText.includes('no tab with id') && allErrorText.includes('background-redux')) ||
      (allErrorText.includes('no tab with id') && allErrorText.includes('background-redux-new')) ||
      allErrorText.includes('chrome-extension://') ||
      allErrorText.includes('lastpass') ||
      (isFromRoute && (lowerErrorMessage.includes('listener') || lowerErrorMessage.includes('message channel') || 
                       lowerErrorMessage.includes('asynchronous response')));
    
    // Suppress cache errors for audio files (harmless browser cache warnings)
    const isCacheError = 
      lowerErrorMessage.includes('err_cache_operation_not_supported') ||
      (lowerErrorMessage.includes('failed to load resource') && lowerErrorMessage.includes('cache')) ||
      lowerErrorString.includes('err_cache_operation_not_supported') ||
      (lowerErrorString.includes('failed to load resource') && lowerErrorString.includes('cache')) ||
      allErrorText.includes('err_cache_operation_not_supported') ||
      (allErrorText.includes('failed to load resource') && allErrorText.includes('cache')) ||
      (allErrorText.includes('boxing-bell') && (allErrorText.includes('cache') || allErrorText.includes('err_cache')));
    
    if (isBrowserExtensionError || isCacheError) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true); // Use capture phase to catch early

  // Also suppress window error events from browser extensions and network errors
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || event.error?.message || '';
    const errorFilename = event.filename || '';
    const errorName = event.error?.name || '';
    const errorTarget = event.target as HTMLElement;
    const errorSrc = (errorTarget as HTMLImageElement | HTMLScriptElement | HTMLAudioElement)?.src || '';
    const errorStack = event.error?.stack || '';
    
    // Suppress cache errors for audio files (harmless browser cache warnings)
    // Handle URL-encoded filenames (e.g., boxing-bell-signals-6115%20(1).mp3)
    const decodedFilename = decodeURIComponent(errorFilename);
    const decodedSrc = decodeURIComponent(errorSrc);
    const lowerErrorMsg = errorMessage.toLowerCase();
    const lowerFilename = errorFilename.toLowerCase();
    const lowerSrc = errorSrc.toLowerCase();
    
    // Check for cache operation errors (case-insensitive, handle URL encoding)
    const isCacheError = 
      lowerErrorMsg.includes('err_cache_operation_not_supported') ||
      lowerErrorMsg.includes('net::err_cache') ||
      (lowerErrorMsg.includes('failed to load resource') && lowerErrorMsg.includes('cache')) ||
      (lowerFilename.includes('boxing-bell') && (lowerErrorMsg.includes('cache') || lowerErrorMsg.includes('err_cache') || lowerErrorMsg.includes('failed to load'))) ||
      (lowerSrc.includes('boxing-bell') && (lowerErrorMsg.includes('cache') || lowerErrorMsg.includes('err_cache') || lowerErrorMsg.includes('failed to load'))) ||
      (decodedFilename.toLowerCase().includes('boxing-bell') && (lowerErrorMsg.includes('cache') || lowerErrorMsg.includes('err_cache') || lowerErrorMsg.includes('failed to load'))) ||
      (decodedSrc.toLowerCase().includes('boxing-bell') && (lowerErrorMsg.includes('cache') || lowerErrorMsg.includes('err_cache') || lowerErrorMsg.includes('failed to load')));
    
    if (isCacheError) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    
    // Suppress 400 errors from resources (likely from browser extensions or network issues)
    // Also suppress 400 errors from fighter_profiles queries (handled gracefully in code)
    if (
      errorMessage.includes('400') ||
      (errorSrc && errorMessage.includes('Failed to load resource') && errorMessage.includes('400'))
    ) {
      // Suppress 400 errors from fighter_profiles queries (RLS or query format issues - handled gracefully)
      if (errorSrc.includes('fighter_profiles') || decodedSrc.includes('fighter_profiles')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      // Suppress other 400 errors if not from our own API endpoints
      if (!errorSrc.includes('supabase.co') && !errorSrc.includes('tantalus')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }
    
    // Suppress 403 errors on logout endpoint (regardless of scope parameter)
    // These occur when session is already missing/expired - expected behavior
    if (
      (errorFilename.includes('/auth/v1/logout') || errorSrc.includes('/auth/v1/logout')) &&
      (errorMessage.includes('403') || errorMessage.includes('Failed to load resource'))
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    
    // Suppress jQuery ($) not defined errors from browser extension content scripts
    if (
      errorMessage.includes('$ is not defined') ||
      errorMessage.includes('ReferenceError: $') ||
      (errorName === 'ReferenceError' && errorMessage.includes('$')) ||
      errorFilename.includes('content-script') ||
      errorFilename.includes('ch-content-script') ||
      errorStack.includes('content-script') ||
      errorStack.includes('ch-content-script')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    
    // Suppress browser extension errors (including background-redux-new.js and chrome-extension:// URLs)
    // Also check for errors from routes (these are often browser extension errors)
    const lowerStack = errorStack.toLowerCase();
    // Check if error is from any route (login, matchmaking, rules, rankings, etc.)
    const isFromRoute = /^\/?\d*[a-z]+/.test(errorFilename) || 
                        errorFilename.includes('/login') || 
                        errorFilename.includes('/matchmaking') || 
                        errorFilename.includes('/rules') ||
                        errorFilename.includes('/rankings') ||
                        errorStack.includes('/login') || 
                        errorStack.includes('/matchmaking') ||
                        errorStack.includes('/rules') ||
                        errorStack.includes('/rankings');
    
    // Check for async response errors (browser extension communication errors)
    const isAsyncResponseErrorWindow = 
      lowerErrorMsg.includes('listener indicated an asynchronous response') ||
      lowerErrorMsg.includes('message channel closed before a response was received') ||
      lowerErrorMsg.includes('a listener indicated an asynchronous response') ||
      lowerErrorMsg.includes('by returning true, but the message channel closed') ||
      (lowerErrorMsg.includes('by returning true') && lowerErrorMsg.includes('message channel closed')) ||
      (lowerErrorMsg.includes('asynchronous response') && lowerErrorMsg.includes('message channel')) ||
      (lowerErrorMsg.includes('listener indicated') && lowerErrorMsg.includes('asynchronous response') && lowerErrorMsg.includes('message channel closed')) ||
      (lowerErrorMsg.includes('listener indicated') && lowerErrorMsg.includes('message channel closed before')) ||
      lowerFilename.includes('background-redux') ||
      lowerFilename.includes('background-redux-new.js') ||
      lowerStack.includes('background-redux') ||
      lowerStack.includes('background-redux-new.js');
    
    if (
      isAsyncResponseErrorWindow ||
      lowerErrorMsg.includes('runtime.lasterror') ||
      lowerErrorMsg.includes('unchecked runtime.lasterror') ||
      (lowerErrorMsg.includes('unchecked runtime') && lowerErrorMsg.includes('cannot create item')) ||
      (lowerErrorMsg.includes('runtime.last') && lowerErrorMsg.includes('cannot create item')) ||
      lowerErrorMsg.includes('cannot create item with duplicate id') ||
      lowerErrorMsg.includes('cannot find menu item') ||
      lowerErrorMsg.includes('no tab with id') ||
      lowerErrorMsg.includes('background-redux') ||
      lowerFilename.includes('background-redux') ||
      lowerFilename.includes('background-redux-new.js') ||
      lowerFilename.includes('chrome-extension://') ||
      lowerFilename.includes('content-script') ||
      lowerFilename.includes('ch-content-script') ||
      lowerFilename.includes('lastpass') ||
      lowerStack.includes('background-redux') ||
      lowerStack.includes('background-redux-new.js') ||
      lowerStack.includes('chrome-extension://') ||
      lowerStack.includes('content-script') ||
      lowerStack.includes('ch-content-script') ||
      lowerStack.includes('lastpass') ||
      (isFromRoute && (lowerErrorMsg.includes('listener') || lowerErrorMsg.includes('message channel') || 
                       lowerErrorMsg.includes('asynchronous response'))) ||
      (event.filename && (event.filename.toLowerCase().includes('background-redux') || 
                          event.filename.toLowerCase().includes('background-redux-new.js') ||
                          event.filename.toLowerCase().includes('chrome-extension://') ||
                          event.filename.toLowerCase().includes('content-script') ||
                          event.filename.toLowerCase().includes('ch-content-script') ||
                          event.filename.toLowerCase().includes('lastpass')))
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();